import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'rememberMe' ? checked : value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const result = await login({
      username: formData.username,
      password: formData.password,
    });

    setIsLoading(false);

    if (result.success) {
      navigate('/');
    }
  };

  if (authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#ffffff',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left Side: Login Form - 65% width, centered */}
      <Box
        sx={{
          flex: '0 0 65%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          padding: '40px 60px',
          '@media (max-width: 960px)': {
            flex: '0 0 100%',
            padding: '40px 24px',
          },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '400px' }}>
          <Box sx={{ mb: 5 }}>
            <Box
              component="img"
              src="/logo.png"
              alt="interaktv"
              sx={{ height: 32, mb: 3, display: 'block' }}
            />
            <Typography
              component="h1"
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: '#1e293b',
                fontSize: '1.75rem',
              }}
            >
              User Login
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.95rem' }}>
              Enter your credentials to access your content.
            </Typography>
          </Box>

          <Box component="form" noValidate onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Email Address"
              name="username"
              autoComplete="email"
              autoFocus
              value={formData.username}
              onChange={handleChange}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ pr: 1.5 }}>
                    <Email sx={{ color: '#94a3b8', fontSize: '20px' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  height: '48px',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                    borderWidth: '1.5px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e1',
                    borderWidth: '1.5px',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#F7941D',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputBase-input': {
                  py: 1.5,
                  '&::placeholder': {
                    opacity: 0.7,
                  },
                },
                '& .MuiInputLabel-root': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                  backgroundColor: '#ffffff',
                  px: 0.5,
                  '&.Mui-focused': {
                    color: '#F7941D',
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ pr: 1.5 }}>
                    <Lock sx={{ color: '#94a3b8', fontSize: '20px' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ pr: 0.5 }}>
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                      disabled={isLoading}
                      tabIndex={-1}
                      sx={{
                        color: '#94a3b8',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: '20px' }} />
                      ) : (
                        <Visibility sx={{ fontSize: '20px' }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  height: '48px',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                    borderWidth: '1.5px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e1',
                    borderWidth: '1.5px',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#F7941D',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputBase-input': {
                  py: 1.5,
                },
                '& .MuiInputLabel-root': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                  backgroundColor: '#ffffff',
                  px: 0.5,
                  '&.Mui-focused': {
                    color: '#F7941D',
                  },
                },
              }}
            />



            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                py: 1.75,
                fontSize: '1rem',
                fontWeight: 600,
                backgroundColor: '#F7941D',
                borderRadius: '8px',
                boxShadow: '0 6px 20px rgba(247, 148, 29, 0.35)',
                textTransform: 'none',
                mb: 4,
                letterSpacing: '0.3px',
                '&:hover': {
                  backgroundColor: '#C57617',
                  boxShadow: '0 10px 28px rgba(247, 148, 29, 0.45)',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&:disabled': {
                  backgroundColor: '#cbd5e1',
                  boxShadow: 'none',
                  color: '#94a3b8',
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>

            <Box sx={{ textAlign: 'center', pt: 2, borderTop: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1.5, fontSize: '0.8rem' }}>
                Don't have an account?
              </Typography>
              <Link
                href="#"
                sx={{
                  fontWeight: 500,
                  textDecoration: 'none',
                  color: '#F7941D',
                  fontSize: '0.9rem',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Contact Admin
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Right Side: Branding - 35% width */}
      <Box
        sx={{
          flex: '0 0 35%',
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '@media (max-width: 960px)': {
            display: 'none',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            opacity: 0.1,
            background: `radial-gradient(circle at 10% 90%, #F7941D 0%, transparent 50%), 
                        radial-gradient(circle at 90% 10%, #F7941D 0%, transparent 50%)`,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: 3, maxWidth: '320px' }}>
          <Box
            sx={{
              display: 'inline-flex',
              backgroundColor: 'white',
              borderRadius: '10px',
              px: 2,
              py: 1.25,
              mb: 2.5,
            }}
          >
            <Box component="img" src="/logo.png" alt="interaktv" sx={{ height: 40 }} />
          </Box>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 400,
              fontSize: '0.95rem',
              lineHeight: 1.7,
              letterSpacing: '0.2px',
            }}
          >
            Upload and manage content with ease. Your audience awaits.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
